import * as widgetActions from '../actions/widget.actions';

import { Action, Store, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AnyFilterItem, isValueItem } from '../reducers/feed.reducer';
import { DialogCloseReason, DialogService } from '@sui/dialog';
import {
  Dimension,
  FilterItem,
  PeriodFilterItem,
  Widget,
} from '../models/core.models';
import { EMPTY, combineLatest, concat, from } from 'rxjs';
import {
  Gap,
  fromUnix,
  getPeriodBounds,
  toUnix,
} from 'src/app/shared/utils/date-utils';
import { NomObjectClass, WidgetType } from '../constants';
import {
  activateFilters,
  deselectFilterItem,
  selectFilterItem,
  toggleFilterDimension,
} from '../actions/feed.actions';
import {
  bufferTime,
  catchError,
  filter,
  groupBy,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  getContentFilters,
  selectCurrentObject,
  selectFeedActiveFilterItems,
  selectFirstAvailableWidgetRow,
  selectSelectedPageId,
  selectSelectedPlaceholderWidget,
  selectSelectedPlaceholderWidgetId,
  selectUser,
  selectWidgetById,
  selectWidgetEditMode,
  selectWidgetSearchString,
  selectWidgets,
  selectWidgetsAsArray,
  selectWidgetsLanguageId,
} from '../reducers';

import { FeedResultsPreviewDialogComponent } from '../components/popups/feed-results-preview-dialog/feed-results-preview-dialog.component';
import { FeedService } from '../services/feed.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from '../services/utils.service';
import { WidgetService } from '../services/widget.service';
import { concatLatestFrom } from '@ngrx/operators';
import { duplicateChartTypesWithVisualization } from '../models/widget.models';
import { of } from 'rxjs';
import { periodChanged } from '../actions/core.actions';
import { selectCurrentObjectCacheEnabled } from 'src/app/core/reducers';

@Injectable()
export class WidgetEffects {
  /**
   * Load list of widgets for a feed
   */
  loadFeedWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.loadWidgets),
      mergeMap((action) =>
        this.widgetService
          .getObjectWidgets(
            action.objectId,
            action.objectClassId,
            action.pageId,
          )
          .pipe(
            map((widgets) => {
              return widgetActions.loadWidgetsSuccess({
                widgets,
                // ! pageId is only for Dashboards
                pageId: action.pageId,
              });
            }),
            catchError(() => EMPTY),
          ),
      ),
    ),
  );

  // * Load ALL widgets for presentation mode (no pageId filter)
  loadAllWidgetsForPresentation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.loadAllWidgetsForPresentation),
      mergeMap((action) =>
        this.widgetService
          .getObjectWidgets(
            action.objectId,
            action.objectClassId,
            // ! No pageId - fetch all widgets
          )
          .pipe(
            map((widgets) => {
              // * Extract unique page IDs from the fetched widgets
              const pageIds = [
                ...new Set(
                  widgets
                    .map((widget) => widget.page_id)
                    .filter(Boolean) as number[],
                ),
              ];

              return widgetActions.loadAllWidgetsForPresentationSuccess({
                widgets,
                pageIds,
              });
            }),
            catchError((error) =>
              of(widgetActions.loadAllWidgetsForPresentationFail({ error })),
            ),
          ),
      ),
    ),
  );

  // * Load a single Widget
  loadWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.loadWidget),
      // Group by widgetId so each widget has its own stream
      groupBy((action: any) => action.widgetId),
      // Merge each group into a higher-order observable that maps to a stream per widgetId
      mergeMap((group$) =>
        group$.pipe(
          // Combine each action with the latest contentFilters, languageId, cache flag
          withLatestFrom(
            this.store.select(getContentFilters),
            this.store.select(selectWidgetsLanguageId),
            this.store.select(selectCurrentObjectCacheEnabled),
          ),
          // Use switchMap to ensure cancellation of ongoing requests for the same widget
          switchMap(
            ([action, contentFilters, widgetsLanguageId, cacheEnabled]) =>
              this.widgetService
                .getWidgetData(
                  action.widgetId,
                  contentFilters,
                  widgetsLanguageId,
                  cacheEnabled,
                )
                .pipe(
                  map((widgetData) =>
                    widgetActions.loadWidgetSuccess({
                      widgetId: action.widgetId,
                      widgetData,
                    }),
                  ),
                  catchError((error) =>
                    of(
                      widgetActions.loadWidgetFail({
                        widgetId: action.widgetId,
                        error,
                      }),
                    ),
                  ),
                ),
          ),
        ),
      ),
    ),
  );

  // * load single widget - metadata/settings
  loadSingleWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.loadSingleWidget),
      mergeMap((action) =>
        this.widgetService.getWidget(action.widgetId).pipe(
          map((widget) => widgetActions.loadSingleWidgetSuccess({ widget })),
          catchError((error) =>
            of(
              widgetActions.loadSingleWidgetFail({
                widgetId: action.widgetId,
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.updateWidget),
      mergeMap((action) =>
        this.widgetService.updateWidget(action.widgetId, action.data).pipe(
          map((widget) =>
            widgetActions.updateWidgetSuccess({
              widgetId: action.widgetId,
              data: widget,
            }),
          ),
          catchError((error) =>
            of(
              widgetActions.updateWidgetFail({
                widgetId: action.widgetId,
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  massUpdateWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.massUpdateWidgets),
      mergeMap((action) =>
        this.widgetService.massUpdate(action.widgetIds, action.data).pipe(
          map((response) =>
            widgetActions.massUpdateWidgetsSuccess({
              widgetIds: action.widgetIds,
              data: action.data,
              countUpdated: response.updated_count || 0,
              widgets: response.widgets, // Optionally include updated widgets in the success action
            }),
          ),
          catchError((error) =>
            of(
              widgetActions.massUpdateWidgetsFail({
                widgetIds: action.widgetIds,
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  massUpdateWidgetsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.massUpdateWidgetsSuccess),
      withLatestFrom(this.store.select(selectWidgetEditMode)),
      tap(([action]) => {
        this.toastService.showSuccess(
          this.translateService.instant(
            'widget.widget-settings-updated-successfully',
            { count: action.countUpdated },
          ),
        );
      }),
      filter(([, editMode]) => !editMode),
      mergeMap(([action]) =>
        from(action.widgetIds).pipe(
          map((widgetId) => widgetActions.loadWidget({ widgetId })),
        ),
      ),
    ),
  );

  massUpdateWidgfeetsFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(widgetActions.massUpdateWidgetsFail),
        tap((action) => {
          this.toastService.showError(
            this.translateService.instant(
              'widget.failed-to-update-widget-settings',
              { error: action.error?.message || '' },
            ),
          );
        }),
      ),
    { dispatch: false },
  );

  updateWidgetDatasource$ = createEffect(() =>
    this.actions$.pipe(
      // Extract the payload (widgetId) from the action
      ofType(
        widgetActions.applyWidgetDatasourceIds,
        widgetActions.applyWidgetFilterIds,
        widgetActions.applyWidgetPivotIds,
        widgetActions.removeWidgetFilter,
        widgetActions.removeWidgetPivot,
      ),

      map((action) => action.widgetId),

      // Use mergeMap to combine the widgetId with latest store data
      mergeMap((widgetId) =>
        of(widgetId).pipe(
          withLatestFrom(this.store.pipe(select(selectWidgetById(widgetId)))),
          map(([widgetId, widget]) => ({ widgetId, widget })),
        ),
      ),

      // Use switchMap to call the service
      switchMap(({ widgetId, widget }) =>
        this.widgetService.updateWidget(widgetId, widget).pipe(
          map((data) =>
            widgetActions.updateWidgetSuccess({
              widgetId: widgetId,
              data,
            }),
          ),
          catchError((error) =>
            of(
              widgetActions.updateWidgetFail({
                widgetId: widgetId,
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateMultipleWidgetsDatasource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        widgetActions.applyAllWidgetsFilterIds,
        widgetActions.applyAllWidgetsPivotIds,
        widgetActions.removeAllWidgetsFilters,
        widgetActions.removeAllWidgetsPivots,
      ),
      map((action) => action.widgetIds),

      // Use mergeMap to handle each widget ID
      mergeMap((widgetIds) =>
        from(widgetIds).pipe(
          // For each widget ID, get the corresponding widget data and call the update service
          mergeMap((widgetId) =>
            of(widgetId).pipe(
              withLatestFrom(
                this.store.pipe(select(selectWidgetById(widgetId))),
              ),
              switchMap(([widgetId, widget]) =>
                this.widgetService.updateWidget(widgetId, widget).pipe(
                  map((data) =>
                    widgetActions.updateWidgetSuccess({
                      widgetId: widgetId,
                      data,
                    }),
                  ),
                  catchError((error) =>
                    of(
                      widgetActions.updateWidgetFail({
                        widgetId: widgetId,
                        error,
                      }),
                    ),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  reloadWidgetAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        widgetActions.updateWidgetSuccess,
        widgetActions.createWidgetSuccess,
        widgetActions.swapWidgetTypeSuccess,
      ),
      withLatestFrom(this.store.select(selectWidgetEditMode)),
      // ! filter out Placeholder widgets BEFORE creating the loadWidget action
      filter(([action]) => {
        const widget = 'widget' in action ? action.widget : null;

        // ! placeholders don't get updated
        if (widget) {
          return widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER;
        }

        // * for updateWidgetSuccess, we know it's a real widget
        return true;
      }),

      filter(([_, editMode]) => !editMode),

      map(([action]) => {
        const widgetId =
          'widgetId' in action ? action.widgetId : action.widget.id;
        return widgetActions.loadWidget({ widgetId });
      }),
    ),
  );

  loadWidgetPresets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.loadWidgetPresets),
      mergeMap((action) =>
        this.widgetService
          .loadWidgetPresets(action.objectTypeId, action.objectClassId)
          .pipe(
            map((widgetPresets) => {
              //console.log(widgetPresets);
              widgetPresets.map((widgetPreset) => {
                try {
                  widgetPreset.chart_types =
                    duplicateChartTypesWithVisualization(
                      widgetPreset.chart_types,
                    );
                } catch (e) {
                  //console.log(e);
                }
              });
              //console.log(widgetPresets);
              return widgetActions.loadWidgetPresetsSuccess({ widgetPresets });
            }),
            catchError(() => EMPTY),
          ),
      ),
    ),
  );

  // ! We need to apply the page_id
  // ! - page_id is optional and if not specified the widget will be created on the first page
  createWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.createWidget),
      withLatestFrom(
        this.store.pipe(select(selectFirstAvailableWidgetRow)),
        this.store.pipe(select(selectSelectedPlaceholderWidgetId)),
        this.store.select(selectSelectedPageId),
        this.store.pipe(select(selectSelectedPlaceholderWidget)),
        // * select the selected page id
      ),
      mergeMap(
        ([action, row, placeholerWidgetId, selectedPageId, widget]: [
          any,
          number,
          string,
          number,
          Widget,
        ]) => {
          // If there is a placeholder widget, use its properties; otherwise, use defaults

          if (widget?.is_fake) {
            placeholerWidgetId = null;
          }

          return this.widgetService
            .createWidget(
              action.chartId,
              action.chartTypeId,
              action.objectId,
              action.objectClassId,
              action.dataSourceObjectId,
              action.dataSourceObjectClassId,

              row,
              placeholerWidgetId,
              widget?.col,
              widget?.sizeX,
              widget?.sizeY,
              selectedPageId,
            )
            .pipe(
              map((widget) => {
                this.toastService.showSuccess(
                  this.translateService.instant(
                    'widget.widget-created-successfully',
                  ),
                );

                return widgetActions.createWidgetSuccess({
                  widget,
                  objectClassId: action.objectClassId,
                });
              }),
              catchError((error) => {
                this.translateService.instant('widget.failed-to-create-widget');

                return of(widgetActions.createWidgetFail({ error }));
              }),
            );
        },
      ),
    ),
  );

  createWidgetSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.createWidgetSuccess),
      withLatestFrom(this.store.pipe(select(selectWidgets))),
      mergeMap(([action, widgets]) => {
        let hasPlaceholder = false;

        // Check if there is a placeholder widget
        for (const widget of widgets.values()) {
          if (widget.widget_type_id === WidgetType.WIDGET_TYPE_PLACEHOLDER) {
            hasPlaceholder = true;
            break;
          }
        }

        // If there is no placeholder and the current widget is not a placeholder
        if (
          action.objectClassId !== NomObjectClass.DASHBOARD &&
          !hasPlaceholder &&
          action.widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER
        ) {
          const { object_id: objectId, object_class_id: objectClassId } =
            action.widget;

          return this.widgetService
            .getDefaultWidgetStructure(objectId, objectClassId, 0, 0, true)
            .pipe(
              map((widget) =>
                widgetActions.createWidgetSuccess({ widget, objectClassId }),
              ),
              catchError((error) =>
                of(widgetActions.createWidgetFail({ error })),
              ),
            );
        }

        return EMPTY;
      }),
    ),
  );

  deleteWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.deleteWidget),
      mergeMap((action) =>
        this.widgetService.deleteWidget(action.widgetId).pipe(
          map(() =>
            widgetActions.deleteWidgetSuccess({ widgetId: action.widgetId }),
          ),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  updateWidgetsOrder$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(widgetActions.updateWidgetsOrder),
        withLatestFrom(
          this.store.pipe(select(selectWidgetsAsArray)),
          this.store.pipe(select(selectWidgetSearchString)),
        ),
        filter(
          ([action, widgets, searchString]) =>
            !searchString || searchString.length === 0,
        ),
        mergeMap(([action, widgets]) =>
          this.widgetService.updateWidgetsOrder(widgets).pipe(
            map(() => widgetActions.updateWidgetsOrderSuccess()),
            catchError(() => EMPTY),
          ),
        ),
      ),
    { dispatch: false },
  );

  resizeEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetResized, widgetActions.widgetsResizedBatch),
      bufferTime(1000),
      filter((actions) => actions.length > 0),
      map(() => widgetActions.updateWidgetsOrder()),
    ),
  );

  repositionEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetsRepositioned),
      map(() => widgetActions.updateWidgetsOrder()),
    ),
  );

  loadWidgetVizType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.loadVizualizationTypes),
      mergeMap((action) =>
        this.widgetService.getVizTypes(action.chartId).pipe(
          map((widgetVizTypes) =>
            widgetActions.loadVizualizationTypesSuccess({
              widgetVizTypes,
              chartId: action.chartId,
            }),
          ),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  loadWidgetPresetGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.loadWidgetPresetGroups),
      mergeMap((action) =>
        this.widgetService.loadWidgetPresetGroups().pipe(
          map((widgetPresetGroups) =>
            widgetActions.loadWidgetPresetGroupsSuccess({
              widgetPresetGroups,
            }),
          ),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  deleteWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.deleteWidgets),
      mergeMap((action) =>
        this.widgetService.deleteWidgets(action.widgetIds).pipe(
          map(() =>
            widgetActions.deleteWidgetsSuccess({ widgetIds: action.widgetIds }),
          ),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  // * Unified Mass Widget Operation Effect - handles both copy and move
  widgetMassOperation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetMassOperation),
      withLatestFrom(this.store.select(selectSelectedPageId)),
      mergeMap(([action, currentPageId]) =>
        this.widgetService.massOperation(action.operations).pipe(
          map(() =>
            widgetActions.widgetMassOperationSuccess({
              operations: action.operations,
              currentPageId,
            }),
          ),
          tap((successAction) => {
            const operations = successAction.operations;
            const count = operations.length;
            const operationType = operations[0].action;
            const scenario = operations[0].scenario;

            // * map scenario to display label
            const scenarioLabel = {
              'same-feed': 'Same Feed',
              'same-dashboard': 'Same Dashboard',
              'other-feeds': 'Other Feeds',
              'other-dashboards': 'Other Dashboards',
            }[scenario];

            const actionLabel = operationType === 'copy' ? 'copied' : 'moved';
            const widgetLabel = count === 1 ? 'widget' : 'widgets';

            this.toastService.showSuccess(
              `${count} ${widgetLabel} ${actionLabel} to ${scenarioLabel}`,
            );
          }),
          catchError((error) => {
            // * Handle 400 errors - 500 erros are handled on general level
            const actionType =
              action.operations[0]?.action === 'copy' ? 'copy' : 'move';
            this.toastService.showError(`Failed to ${actionType} widgets`);

            console.error(
              'Mass operation failed:',
              error,
              'Operations:',
              action.operations,
            );

            return of(
              widgetActions.widgetMassOperationFail({
                operations: action.operations,
                error,
              }),
            );
          }),
        ),
      ),
    ),
  );

  // * reload Widgets when copying to the same view (same Dashboard page or same Feed)
  reloadWidgetsAfterCopyToSameView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetMassOperationSuccess),
      withLatestFrom(this.store.select(selectCurrentObject)),
      filter(([{ operations, currentPageId }, currentObject]) => {
        // * check if any operation is copying to the current view
        return operations.some((op) => {
          if (op.action !== 'copy') return false;

          // * same Dashboard: check if copying to current Dashboard AND current page
          if (op.scenario === 'same-dashboard') {
            return (
              currentPageId &&
              op.target_object_ids[0] === currentObject.id &&
              op.target_page_id === currentPageId
            );
          }

          // * same Feed: check if copying to current Feed
          if (op.scenario === 'same-feed') {
            return op.target_object_ids[0] === currentObject.id;
          }

          // * other scenarios -> copying to different Objects, no reload needed
          return false;
        });
      }),
      map(([{ currentPageId }, currentObject]) => {
        return widgetActions.loadWidgets({
          objectId: currentObject.id,
          objectClassId: currentObject.object_class_id,
          pageId: currentPageId,
        });
      }),
    ),
  );

  activatePivotFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.activatePivotFeed),
      withLatestFrom(this.store.pipe(select(selectUser))),
      switchMap(([action, user]) =>
        this.feedService.getFeed(user.id, action.pivotFeedId).pipe(
          map((feed) => {
            // Dispatch the toggleFilterDimension action with the loaded feed

            return toggleFilterDimension({
              dimension: feed as Dimension,
              activate: false,
              isPivot: true,
            });
          }),
          catchError((error) => {
            // Show error toast and complete the effect
            this.toastService.showError('Activate Pivot Feed Failed');
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  widgetClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.htmlWidgetClicked),
      map(({ payload }) => payload),
      // parse inputs first
      map((record) => {
        const widgetId = record.widget_id as string;
        const propertyUri = record.property_uri as string;
        const rawValue = record[record.property_uri];
        const canUse =
          typeof rawValue === 'number' || typeof rawValue === 'string';

        const filterItem: AnyFilterItem | null = canUse
          ? {
              checked: true,
              kind: 'value',
              count_posts: record.y,
              property_uri: propertyUri,
              raw_value: String(rawValue),
              title: record.name,
              active: false,
              type: 'include',
            }
          : null;

        return { widgetId, propertyUri, filterItem };
      }),
      concatLatestFrom(({ widgetId }) =>
        this.store.select(selectWidgetById(widgetId)),
      ),
      mergeMap(([{ propertyUri, filterItem }, widget]) => {
        const out: Action[] = [];

        if (filterItem) {
          out.push(
            selectFilterItem({
              propertyUri,
              filterItem,
              mode: 'include',
            }),
          );
        }

        const widgetFilterDimensions = widget?.dimensions_filter ?? [];
        if (widgetFilterDimensions.length) {
          for (const filterDimension of widgetFilterDimensions) {
            out.push(
              toggleFilterDimension({
                dimension: filterDimension,
                activate: true,
                isPivot: false,
              }),
            );
          }
        }
        out.push(activateFilters());

        return out;
      }),
    ),
  );

  loadWidgetObject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.loadWidgetObject),
      mergeMap(({ widgetId }) =>
        this.widgetService.getWidget(widgetId).pipe(
          map((widget) => widgetActions.loadWidgetObjectSuccess({ widget })),
          catchError((error) =>
            of(widgetActions.loadWidgetObjectFail({ widgetId, error })),
          ),
        ),
      ),
    ),
  );

  widgetPointClickedResolve$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetPointClicked),

      // get widget from store (may be undefined)
      concatLatestFrom(({ widgetId }) =>
        this.store.select(selectWidgetById(widgetId)),
      ),

      switchMap(([{ widgetId, options }, widget]) => {
        // if widget already in store -> continue immediately
        if (widget) {
          return of(
            widgetActions.widgetPointClickedResolved({
              widgetId,
              options,
              widget,
            }),
          );
        }

        // otherwise: dispatch load + wait for success for this widgetId
        return concat(
          of(widgetActions.loadWidgetObject({ widgetId })),

          this.actions$.pipe(
            ofType(widgetActions.loadWidgetObjectSuccess),
            take(1),
            map((a) =>
              widgetActions.widgetPointClickedResolved({
                widgetId,
                options,
                widget: a.widget,
              }),
            ),
          ),
        );
      }),
    ),
  );

  widgetPointClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.widgetPointClickedResolved),

      concatLatestFrom(() => [
        this.store.select(selectFeedActiveFilterItems),
        this.store.select(getContentFilters),
        this.store.select(selectCurrentObject),
      ]),

      mergeMap(
        ([
          { widgetId, options: pointOptions, widget },
          items,
          contentFilters,
          currentObject,
        ]) => {
          const {
            datetime: isDateTime,
            name: dateValue,
            value_name: valueName,
            raw: rawValue,
            property_uri: propertyUri,
            pivot_query_id: pivotFeedId,
            zoom_start: zoomStart,
            zoom_end: zoomEnd,
            is_reset_zoom: isResetZoom,
          } = pointOptions as any;

          const isZoom = zoomStart !== undefined && zoomEnd !== undefined;

          const datasourceIds = widget?.datasources?.map((ds) => ds.id) ?? [];

          const pointDsId = (pointOptions as any).dsid;
          const targetFeedId = pointDsId || datasourceIds[0];
          const hasDifferentDatasource =
            targetFeedId && currentObject?.id !== targetFeedId;

          const actions: Action[] = [];

          const isPeriodField = UtilsService.isPeriodField(propertyUri || '');
          const shouldDispatchFilter =
            propertyUri && !isPeriodField && !pivotFeedId;

          if (!isZoom && shouldDispatchFilter) {
            const filterItem: FilterItem = {
              title: valueName,
              kind: 'value',
              raw_value: rawValue,
              count_posts: 0,
              icon: 'filter_icon',
              checked: true,
              active: false,
              property_uri: propertyUri,
              type: 'include',
            };

            actions.push(
              selectFilterItem({ propertyUri, filterItem, mode: 'include' }),
            );
          }
          if (widget?.dimensions_filter?.length) {
            for (const dimension of widget.dimensions_filter) {
              actions.push(
                toggleFilterDimension({
                  dimension,
                  activate: false,
                  isPivot: false,
                }),
              );
            }
          }

          if (isZoom) {
            const periodField = isPeriodField
              ? propertyUri
              : 'post_time_published';

            const start: Date = fromUnix(zoomStart);
            const end: Date = fromUnix(zoomEnd);

            const periodFilterItem: PeriodFilterItem = {
              kind: 'period',
              title:
                valueName ?? `${start.toISOString()} - ${end.toISOString()}`,
              time_start: zoomStart,
              time_end: zoomEnd,
              checked: true,
              active: false,
              property_uri: periodField,
              type: 'include',
            };

            if (!isResetZoom) {
              const cfStart = contentFilters?.time_start;
              const cfEnd = contentFilters?.time_end;

              if (
                typeof cfStart === 'number' &&
                typeof cfEnd === 'number' &&
                (zoomStart < cfStart || zoomEnd > cfEnd)
              ) {
                actions.push(
                  periodChanged({
                    period: {
                      startTimestamp: Math.min(cfStart, zoomStart),
                      endTimestamp: Math.max(cfEnd, zoomEnd),
                    },
                  }),
                );
              }

              actions.push(
                selectFilterItem({
                  propertyUri: periodField,
                  filterItem: periodFilterItem,
                  mode: 'include',
                }),
              );
            } else {
              actions.push(
                deselectFilterItem({
                  propertyUri: periodField,
                  filterItem: periodFilterItem,
                  mode: 'include',
                }),
              );
            }
          } else if (isDateTime) {
            const currentGap: Gap =
              (widget.settings?.['period_gap'] as Gap) || '+1DAY';
            const { startDate, endDate } = getPeriodBounds(
              dateValue,
              currentGap,
            );

            const clickedStart = toUnix(startDate);
            const clickedEnd = toUnix(endDate);

            const cfStart = contentFilters?.time_start; // unix
            const cfEnd = contentFilters?.time_end; // unix

            // If clicked period is outside current contentFilters range -> expand range
            if (
              typeof cfStart === 'number' &&
              typeof cfEnd === 'number' &&
              (clickedStart < cfStart || clickedEnd > cfEnd)
            ) {
              actions.push(
                periodChanged({
                  period: {
                    startTimestamp: Math.min(cfStart, clickedStart),
                    endTimestamp: Math.max(cfEnd, clickedEnd),
                  },
                }),
              );
            }

            const periodField = isPeriodField
              ? propertyUri
              : 'post_time_published';

            const periodFilterItem: PeriodFilterItem = {
              kind: 'period',
              title:
                valueName ??
                `${startDate.toISOString()} - ${endDate.toISOString()}`,
              time_start: toUnix(startDate),
              time_end: toUnix(endDate),
              checked: true,
              active: false,
              property_uri: periodField,
              type: 'include',
            };

            actions.push(
              selectFilterItem({
                propertyUri,
                filterItem: periodFilterItem,
                mode: 'include',
              }),
            );
          }

          if (pivotFeedId) {
            actions.push(
              widgetActions.activatePivotFeed({ widgetId, pivotFeedId }),
            );
          }

          const isNewItem =
            shouldDispatchFilter &&
            !items.some(
              (item) =>
                isValueItem(item) &&
                item.property_uri === propertyUri &&
                item.raw_value === rawValue,
            );

          const shouldActivateFilters =
            isNewItem || items.length === 0 || isDateTime || !!pivotFeedId;

          if (hasDifferentDatasource) {
            const pointFilters = {
              ...(contentFilters || {}),
            };

            const isPeriodField = UtilsService.isPeriodField(propertyUri || '');

            // Value filter from point click
            if (
              propertyUri &&
              rawValue !== undefined &&
              !isPeriodField &&
              !pivotFeedId
            ) {
              pointFilters[propertyUri] = { include: [rawValue], exclude: [] };
            }

            // Period filter from datetime point click
            if (isDateTime) {
              const currentGap: Gap =
                (widget.settings?.['period_gap'] as Gap) || '+1DAY';
              const { startDate, endDate } = getPeriodBounds(
                dateValue,
                currentGap,
              );
              const periodField = isPeriodField
                ? propertyUri
                : 'post_time_published';
              pointFilters[periodField] = {
                include: [{ start: toUnix(startDate), end: toUnix(endDate) }],
                exclude: [],
              };
            }

            const dialogRef = this.dialogService.openSimpleDialog(
              FeedResultsPreviewDialogComponent,
              {
                data: {
                  feedId: targetFeedId,
                  contentFilters: {
                    time_start: pointFilters?.time_start,
                    time_end: pointFilters?.time_end,
                    sort: pointFilters?.sort,
                    order: pointFilters?.order,
                    ...(Object.keys(pointFilters).length > 0
                      ? { filters: pointFilters }
                      : {}),
                  },

                  pivotFeedId: pivotFeedId,
                  filterDimensions: widget?.dimensions_filter ?? [],
                  filterLabel: valueName,
                },
                size: 'large',
              },
            );

            return dialogRef.closed.pipe(
              take(1),
              switchMap((event) => {
                if (
                  event.reason !== DialogCloseReason.Confirm ||
                  !(event.data as any)?.navigate
                ) {
                  return EMPTY;
                }

                const workspaceId = (event.data as any).workspaceId;

                // Navigate first, then dispatch filter actions after navigation succeeds

                return from(
                  this.router.navigate([
                    'analytics',
                    'workspace',
                    workspaceId,
                    'feed',
                    targetFeedId,
                  ]),
                ).pipe(
                  filter((success) => success),
                  switchMap(() => {
                    if (pivotFeedId && shouldActivateFilters) {
                      return concat(
                        from(actions),
                        this.actions$.pipe(
                          ofType(toggleFilterDimension),
                          filter((action) => action.isPivot === true),
                          take(1),
                          map(() => activateFilters()),
                        ),
                      );
                    }

                    if (shouldActivateFilters) {
                      actions.push(activateFilters());
                    }

                    return from(actions);
                  }),
                );
              }),
            );
          }

          // "wait" when pivotFeedId is present
          if (pivotFeedId && shouldActivateFilters) {
            return concat(
              // dispatch everything you already queued (includes activatePivotFeed)
              from(actions),

              // then wait for pivot load success/resolved toggleFilterDimension (only the pivot trigger)
              this.actions$.pipe(
                ofType(toggleFilterDimension),
                filter((action) => action.isPivot === true),
                take(1),
                map(() => activateFilters()),
              ),
            );
          }

          // normal path (no pivot waiting)
          if (shouldActivateFilters) {
            actions.push(activateFilters());
          }

          return actions;
        },
      ),
    ),
  );

  // * Swap Widget Type - change widget preset/visualization
  swapWidgetType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetActions.swapWidgetType),
      mergeMap((action) =>
        this.widgetService
          .swapWidgetType(action.widgetId, action.chartId, action.chartTypeId)
          .pipe(
            map((widget) => {
              this.toastService.showSuccess(
                this.translateService.instant(
                  'widget.widget-type-swapped-successfully',
                ),
              );

              return widgetActions.swapWidgetTypeSuccess({
                oldWidgetId: action.widgetId,
                widget,
              });
            }),
            catchError((error) => {
              this.toastService.showError(
                this.translateService.instant(
                  'widget.failed-to-swap-widget-type',
                ),
              );

              return of(
                widgetActions.swapWidgetTypeFail({
                  widgetId: action.widgetId,
                  error,
                }),
              );
            }),
          ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private widgetService: WidgetService,
    private store: Store,
    private toastService: ToastService,
    private translateService: TranslateService,
    private feedService: FeedService,
    private utilsService: UtilsService,
    private dialogService: DialogService,
    private router: Router,
  ) {}
}
